import React, { useCallback, useState } from 'react';
import MaterialIcon from '../../elements/MaterialIcon.jsx';
import LadderLocalStorage from '../../../utilities/LadderLocalStorage';
import { bottomBarEntries, sectionGroups } from './sectionGroups';

/**
 * The app's sections, as a sidebar on desktop or a bar along the bottom on a phone.
 *
 * Each item is a real link to its section's url, so a middle or modified click opens it in another
 * tab. A plain click stays in the page and goes through `onSelect` instead.
 *
 * `sections` is a list of { slug, label, icon, group? }. `footerSections` sit at the bottom of the
 * sidebar. Sections of one group (sectionGroups.js) are listed in the sidebar under the group's name,
 * and in the bottom bar as one entry for the group, which goes back to `lastVisited[group]`.
 */
export default function MainNav({ sections, footerSections = [], activeSlug, onSelect, variant = 'sidebar', baseUrl = '', lastVisited = {} }) {
	const [collapsed, setCollapsed] = useState(() => readCollapsed());

	const toggleCollapsed = useCallback(() => {
		setCollapsed((was) => {
			writeCollapsed(!was);
			return !was;
		});
	}, []);

	const renderItem = (section) => (
		<li key={section.key || section.slug} className={section.group ? 'is-grouped' : undefined}>
			<SectionLink
				slug={section.slug}
				label={section.label}
				icon={section.icon}
				active={section.active !== undefined ? section.active : section.slug === activeSlug}
				onSelect={onSelect}
				baseUrl={baseUrl}
				title={collapsed && variant === 'sidebar' ? section.label : undefined}
			/>
		</li>
	);

	if(variant === 'bottom') {
		return (
			<div role='navigation' className='main-nav main-nav--bottom' aria-label='Sections'>
				<ul>{bottomBarEntries(sections, activeSlug, lastVisited).map(renderItem)}</ul>
			</div>
		);
	}

	// A group's name heads its first section; collapsed to icons, a rule stands in for it.
	const sidebarItems = [];
	sections.forEach((section, index) => {
		if(section.group && (index === 0 || sections[index - 1].group !== section.group)) {
			sidebarItems.push(
				<li key={`group-${section.group}`} className='main-nav__group' aria-hidden='true'>
					<span className='main-nav__label'>{sectionGroups[section.group].label}</span>
				</li>
			);
		}
		sidebarItems.push(renderItem(section));
	});

	return (
		<div role='navigation' className={`main-nav main-nav--sidebar ${collapsed ? 'is-collapsed' : ''}`} aria-label='Sections'>
			<div className='main-nav__brand'>
				<img src={`${baseUrl}/images/icon192.png`} alt='' />
				<span className='main-nav__label'>Budget</span>
			</div>
			<ul className='main-nav__list'>{sidebarItems}</ul>
			<ul className='main-nav__footer'>
				{footerSections.map(renderItem)}
				<li>
					<button
						type='button'
						className='shell-control main-nav__item main-nav__collapse'
						aria-label={collapsed ? 'Expand the sidebar' : 'Collapse the sidebar'}
						onClick={toggleCollapsed}
					>
						<MaterialIcon className='main-nav__icon'>{collapsed ? 'chevron_right' : 'chevron_left'}</MaterialIcon>
						<span className='main-nav__label'>Collapse</span>
					</button>
				</li>
			</ul>
		</div>
	);
}

/** A link to one section: its url for the browser, `onSelect(slug)` for a plain click. */
export function SectionLink({ slug, label, icon, active, onSelect, baseUrl = '', title, className = 'main-nav__item' }) {
	const onClick = (event) => {
		if(event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
			return;
		}
		event.preventDefault();
		onSelect(slug);
	};
	return (
		<a
			href={`${baseUrl}/dashboard/${slug}`}
			className={`${className} ${active ? 'is-active' : ''}`}
			aria-current={active ? 'page' : undefined}
			aria-label={label}
			title={title}
			onClick={onClick}
		>
			<MaterialIcon className='main-nav__icon'>{icon}</MaterialIcon>
			<span className='main-nav__label'>{label}</span>
		</a>
	);
}

function readCollapsed() {
	try {
		return LadderLocalStorage.getItem('mainNavCollapsed', false) === true;
	} catch(error) {
		return false;
	}
}

function writeCollapsed(value) {
	try {
		LadderLocalStorage.setItem('mainNavCollapsed', value);
	} catch(error) {
		// Not remembering the choice is fine.
	}
}
